import { Card, CircularProgress, Grid, TextField } from "@mui/material";
import { useFormik } from "formik";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import UploadField from "components/FormField/FileUploadField";
import { ETypeContent } from "../../pages/content/content.type";
import useUploadMapImage, { useGetAllContent, useUpdateContents } from "pages/content/content.hook";
import { useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

type Props = {
  title: string;
  contentId: ETypeContent;
};

export default function MapUpdate({ title, contentId }: Props) {
  const { data, isLoading } = useGetAllContent();
  const uploadMutation = useUploadMapImage();
  const updateContentsMutation = useUpdateContents();
  const [currentUrl, setCurrentUrl] = useState<string | undefined>(
    data?.find(c => c.id === contentId)?.link1
  );
  const [zenbusUrl, setZenbusUrl] = useState<string>(
    data?.find(c => c.id === contentId)?.link2 ?? ""
  );

  const formik = useFormik({
    initialValues: { file: null as File | null },
    onSubmit: async (values, { resetForm }) => {
      if (!values.file) return;

      const { link1 } = await uploadMutation.mutateAsync({
        id: contentId,
        file: values.file,
      });
      setCurrentUrl(link1);
      resetForm({ values: { file: null } });
    },
  });

  if (isLoading) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress color="inherit" />
      </MDBox>
    );
  }

  return (
    <MDBox mt={4}>
      <Card style={{ overflowY: "auto" }}>
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <MDBox my={3} m={4}>
            <MDBox mb={6}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} lg={6}>
                  <MDTypography variant="h4" fontWeight="medium">
                    {title}
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>

            <MDBox mt={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <UploadField
                    onUpload={(file: File) => formik.setFieldValue("file", file)}
                    newUploadLabel="Ajouter une carte"
                    updateUploadLabel="Modifier la carte"
                    noFilelabel="Aucune carte encore téléversée"
                    seeFileLabel="Consulter la carte actuelle"
                    initialUrl={currentUrl}
                  />
                </Grid>
              </Grid>
            </MDBox>

            {contentId === ETypeContent.mapCircuit && (
              <MDBox pt={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={1}>
                  URL iFrame navettes (Zenbus)
                </MDTypography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="https://zenbus.net/h2o?iframe=true"
                      value={zenbusUrl}
                      onChange={(e) => setZenbusUrl(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      disabled={updateContentsMutation.isLoading}
                      onClick={() => updateContentsMutation.mutate([{ id: contentId, link2: zenbusUrl }])}
                    >
                      Enregistrer
                      {updateContentsMutation.isLoading && (
                        <CircularProgress size={18} sx={{ color: "inherit", ml: 1 }} />
                      )}
                    </MDButton>
                  </Grid>
                </Grid>
                <MDBox pt={2}>
                  <MDButton
                    color="secondary"
                    variant="outlined"
                    onClick={() => {
                      const tabs = document.querySelectorAll("[role=tab]");
                      tabs.forEach((tab: any) => {
                        if (tab.innerText.toLowerCase().includes("circuit navette")) {
                          window.scrollTo(0,0);
                          tab.click();
                        }
                      });
                    }}
                    startIcon={<ArrowForwardIcon />}
                  >
                    Modifier les horaires et circuits
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
          <MDBox p={2} display="flex" justifyContent="flex-end">
            <MDButton
              disabled={!formik.values.file || uploadMutation.isLoading}
              type="submit"
              variant="gradient"
              color="info"
            >
              Valider
              {uploadMutation.isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "inherit",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </MDButton>
          </MDBox>
        </form>
      </Card>
    </MDBox>
  );
}